import moment from 'moment-timezone';
import React, { useEffect, useMemo, useState } from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';

import BillCard from './BillCard';
import OverdueNotificationCard from './OverdueNotificationCard';

import {
  useAutoDebitRegistrationQuery,
  useBillStatementsQuery,
} from 'apollo/queries/autogenerate/hooks';
import {
  AutoDebitSettlement,
  AutoDebitStatus,
  BillStatement,
  BillStatementStatus,
  BillStatementStatusV2,
  Currency,
} from 'apollo/queries/autogenerate/schemas';
import MaximumAmountOfCombiniPaymentCard from 'shared/modules/bill/components/organisms/MaximumAmountOfCombiniPaymentCard';
import isHighPriceBill from 'shared/modules/bill/utils/is-high-price-bill';
import { shouldHidePaymentMethod } from 'shared/modules/bill/utils/should-hide-payment-method';
import Tabs, { Tab } from 'shared/modules/common/components/atoms/Tabs';

const HIGHLIGHTED_BILLS_COUNT = 3;

type Props = {
  onOverdueNotificationItemPress?: (billStatement: BillStatement) => void;
  onOverdueNotificationLinkPress?: () => void;
  onItemDetailPress?: (billStatement: BillStatement) => void;
  onItemPayButtonPress?: (billStatement: BillStatement) => void;
  onSeeAllButtonPress?: () => void;
  onPaymentMethodPress: (billId: string) => void;
  style?: StyleProp<ViewStyle>;
};

const HighlightedBills = (props: Props) => {
  // Solely used to display the correct payment method on the placeholder bill.
  const { data: autoDebitRegistrationData } = useAutoDebitRegistrationQuery({
    fetchPolicy: 'cache-and-network',
  });

  const { data: billStatementsData, loading } = useBillStatementsQuery({
    fetchPolicy: 'cache-and-network',
  });

  const [formattedData, setFormattedData] = useState<BillStatement[]>([]);

  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const formatData = () => {
    // By for most accounts, the backend returns bills in chronological order
    const billStatements = [
      ...(billStatementsData?.billStatements || []),
    ] as BillStatement[];

    /**
     * WARNING: The backend does not return bills chronologically on some accounts.
     * Until that issue is fixed, this code snippet is a temporary mitigation.
     * Sort the bills in chronological order with the most recent bills on top.
     *
     * This works but this is only a patch on a BE problem and should be fixed on that side ASAP.
     * This has a potentially huge performance impact.
     */
    billStatements.sort((billA, billB) => {
      return new Date(billA.dueAt).getTime() - new Date(billB.dueAt).getTime();
    });

    // Filter the overdue and non-overdue bills into two separate groups
    const overdues: BillStatement[] = [];
    const nonOverdues: BillStatement[] = [];

    billStatements.forEach((bill) => {
      if (bill.statusV2 === BillStatementStatusV2.OVERDUE) {
        overdues.push(bill);

        return;
      }

      nonOverdues.push(bill);
    });

    // If there is no bill for the current month yet, then create a placeholder bill
    const getPlaceholderBills = () => {
      const today = new Date();
      const placeHolderDueAt = new Date();

      // This order is important
      // Set the date to 10th first then set the month
      // If we don't do it that way, this can cause an issue on months with only 30 days and Feb
      // Where JS will roll over to the month if that month has less days
      // Ex. Setting Sept 31 will roll over to Oct 1 since Sept only has 30 days
      placeHolderDueAt.setDate(10);
      placeHolderDueAt.setMonth(today.getMonth() + 1);

      const placeholder = {
        // __typename: 'BillStatement',
        id: '',
        amount: 0,
        unpaidAmount: 0,
        unpaidAmountActual: 0,
        outstandingAmount: 0,
        lateFeeAmount: 0,
        currency: Currency.JPY,
        period: {
          // __typename: 'Period',
          start: today,
          end: today,
        },
        status: BillStatementStatus.CREATED,
        statusV2: BillStatementStatusV2.OUTSTANDING,
        dueAt: placeHolderDueAt,
        billingCycle: moment(today).tz('Asia/Tokyo').format('YYYY-MM'),
        combiniSettlementAvailable: false,
        bankTransferSettlementAvailable: false,
        charges: [],
        lateFeeCharges: [],
        // If user has auto-debit enabled, the placeholder bill should show the payment method is auto-debit.
        // Just an empty object is good enough, we just need to check if it's not null.
        autoDebitSettlement:
          autoDebitRegistrationData?.autoDebitRegistration.status ===
          AutoDebitStatus.ACTIVE
            ? ({} as AutoDebitSettlement)
            : null,
        totalCredits: 0,
      };

      if (!billStatements.length) {
        return [placeholder];
      }

      // If there more overdue bills don't add a placeholder
      if (overdues.length >= HIGHLIGHTED_BILLS_COUNT) {
        return [];
      }

      const latestBillEndPeriod =
        billStatements[billStatements.length - 1].period.end;
      const isLatestBillForCurrentMonth =
        new Date(latestBillEndPeriod).getMonth() === new Date().getMonth();

      if (!isLatestBillForCurrentMonth) {
        return [placeholder];
      }

      return [];
    };

    /**
     * Creates an array of both overdue and non-overdue bills
     * Max array length is HIGHLIGHTED_BILLS_COUNT
     * Overdue bills first then available slots will be filled with the latest non-overdue bills
     */
    const getFormattedBills = () => {
      // If there are more than overdue bills than the HIGHLIGHTED_BILLS_COUNT, then just show the oldest ones
      let arr = [...overdues.slice(0, HIGHLIGHTED_BILLS_COUNT)];

      if (arr.length < HIGHLIGHTED_BILLS_COUNT) {
        const nonOverdueBillsSliceIndex =
          -HIGHLIGHTED_BILLS_COUNT + arr.length + getPlaceholderBills().length;

        if (nonOverdueBillsSliceIndex < 0) {
          arr = [...arr, ...nonOverdues.slice(nonOverdueBillsSliceIndex)];
        }
      }

      arr = [...arr, ...getPlaceholderBills()];

      // Finally, sort the formatted bills by date
      arr.sort(
        (billA, billB) =>
          new Date(billA.period.end).getTime() -
          new Date(billB.period.end).getTime()
      );

      return arr;
    };

    const bills = getFormattedBills();

    setFormattedData(bills);
  };

  // We want to highlight the unpaid bills (overdue or not)
  const unpaidBills = useMemo(
    () =>
      formattedData.filter(
        (bill) => bill.statusV2 !== BillStatementStatusV2.PAID
      ),
    [formattedData]
  );

  // By checking the unpaidBills above, if there is more than one, we want to force the user
  // to pay the oldest unpaid bill first.
  const oldestUnpaidBillIndex = useMemo(
    () =>
      formattedData.findIndex(
        (bill) => bill.statusV2 !== BillStatementStatusV2.PAID
      ) || 0,
    [formattedData]
  );

  // If there are overdue bills, we should show the OverdueNotificationCard to notify the user
  // that he has overdue bills.
  const overdueBills = useMemo(
    () =>
      unpaidBills.filter(
        (bill) => bill.statusV2 === BillStatementStatusV2.OVERDUE
      ),
    [unpaidBills]
  );

  const billsOver300KNotOverdue = useMemo(
    () =>
      unpaidBills.filter(
        (bill) =>
          bill.statusV2 === BillStatementStatusV2.OUTSTANDING &&
          isHighPriceBill(bill)
      ),
    [unpaidBills]
  );

  // Scroll the Tabs component automatically to the oldest unpaid bill.
  useEffect(() => {
    setActiveTabIndex(oldestUnpaidBillIndex);
  }, [oldestUnpaidBillIndex]);

  useEffect(() => {
    if (!loading) {
      formatData();
    }
  }, [loading]);

  if (loading) {
    return null;
  }

  return (
    <View style={props.style}>
      {billsOver300KNotOverdue.length > 0 && (
        <MaximumAmountOfCombiniPaymentCard />
      )}
      {overdueBills.length > 0 && (
        <OverdueNotificationCard
          overdueBills={overdueBills}
          onItemPress={props.onOverdueNotificationItemPress}
          onLinkPress={props.onOverdueNotificationLinkPress}
          style={style.overdueNotificationCard}
          id="highlighted-bills-overdue-notification"
        />
      )}
      <View style={style.billsTabs}>
        <Tabs
          style={style.tabs}
          activeTabIndex={activeTabIndex}
          onTabChange={(index) => setActiveTabIndex(index)}
          id="highlighted-bills-tabs"
        >
          {formattedData.map((bill, index) => {
            /**
             * If there are multiple unpaid highlighted bills,
             * disable the Pay button except the oldest
             *
             * If there is only one unpaid bill, then it automatically is the oldest
             */
            const isOldestUnpaidBill =
              bill.id === formattedData[oldestUnpaidBillIndex].id;

            const hidePaymentMethod = shouldHidePaymentMethod(
              bill.unpaidAmount,
              bill.statusV2
            );

            return (
              <Tab
                label={`${new Date(bill.period.end).getMonth() + 1}月分`}
                key={bill.id}
              >
                <BillCard
                  bill={bill}
                  isOldestUnpaidBill={isOldestUnpaidBill}
                  isHighlightedBill
                  onDetailPress={props.onItemDetailPress}
                  onPayButtonPress={props.onItemPayButtonPress}
                  onPaymentMethodPress={props.onPaymentMethodPress}
                  style={style.card}
                  id={`highlighted-bills-${index}`}
                  showPaymentMethod={!hidePaymentMethod}
                />
              </Tab>
            );
          })}
        </Tabs>
      </View>
    </View>
  );
};

const style = StyleSheet.create({
  overdueNotificationCard: {
    marginBottom: 16,
  },
  billsTabs: {
    position: 'relative',
  },
  card: {
    borderRadius: 24,
    // Will be used when cards are in tabs
    flex: 1,
    justifyContent: 'flex-start',
    marginHorizontal: 16,
  },
  tabs: {
    marginHorizontal: -16,
  },
});

export default HighlightedBills;
